class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this._closedByUser) return;

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.classList.add('animate', 'active');

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    document.documentElement.classList.add('overflow-hidden');
  }

  close() {
    this._closedByUser = true;
    this.classList.remove('animate', 'active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.documentElement.classList.remove('overflow-hidden');

    setTimeout(() => {
      this._closedByUser = false;
    }, 400);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Invalidate any in-flight stale renders (e.g. onCartUpdate racing with this render)
    if (typeof window.__cartDrawerRenderEpoch !== 'undefined') ++window.__cartDrawerRenderEpoch;

    // Ensure drawer is not in empty state when rendering new contents
    this.classList.remove('is-empty');
    this.productId = parsedState.id;

    const currentCartItems = this.querySelector('.cart-drawer-items');
    const currentFooter = this.querySelector('.drawer__footer');

    // Parse the new HTML from section rendering API
    const newDoc = new DOMParser().parseFromString(parsedState.sections['cart-drawer'], 'text/html');

    // Only update cart items (NOT the whole #CartDrawer — blocks are not in the API response)
    const newCartItems = newDoc.querySelector('.cart-drawer-items');
    if (newCartItems && currentCartItems) {
      currentCartItems.innerHTML = newCartItems.innerHTML;
    }

    // Update drawer footer (totals, checkout button)
    const newFooter = newDoc.querySelector('.drawer__footer');
    if (newFooter && currentFooter) {
      currentFooter.outerHTML = newFooter.outerHTML;
    } else if (newFooter && !currentFooter) {
      const drawerInner = this.querySelector('.drawer__inner');
      if (drawerInner) drawerInner.insertAdjacentHTML('beforeend', newFooter.outerHTML);
    }

    // Update cart icon bubble
    const cartBubble = document.getElementById('cart-icon-bubble');
    if (cartBubble && parsedState.sections['cart-icon-bubble']) {
      const newBubble = new DOMParser()
        .parseFromString(parsedState.sections['cart-icon-bubble'], 'text/html')
        .querySelector('.shopify-section');
      if (newBubble) cartBubble.innerHTML = newBubble.innerHTML;
    }

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
      // Re-init COS cart modules (timer, free shipping, delivery estimation, etc.)
      if (typeof CosCartModules !== 'undefined' && CosCartModules.init) {
        CosCartModules.init();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.cart-drawer-items',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__footer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  enableLoading(line) {
    super.enableLoading(line);
    // Add smooth fade to the whole drawer content
    const wrapper = this.closest('.cart-drawer-items');
    if (wrapper) wrapper.classList.add('is-updating');
  }

  disableLoading(line) {
    try { super.disableLoading(line); } catch (e) { /* le retrait de is-updating doit toujours avoir lieu */ }
    // Fade content back in — use document.querySelector because `this` may be
    // detached from the DOM after innerHTML replacement in updateQuantity()
    requestAnimationFrame(() => {
      const wrapper = document.querySelector('.cart-drawer-items');
      if (wrapper) wrapper.classList.remove('is-updating');
    });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
